import React, { useState, useEffect } from 'react';
import {
  Building2,
  MapPin,
  Home,
  Navigation,
  Hash,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  FileText,
  Map,
  ArrowLeft,
  Info,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';

import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// 🔹 Fix default Leaflet marker icons (so they show correctly in bundlers)
const defaultMarkerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// 🔹 Handles clicking on the map and shows the marker
const LocationMarker = ({ position, onChange }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onChange({ lat, lng });
    },
  });

  return position ? <Marker position={position} icon={defaultMarkerIcon} /> : null;
};

// 🔹 Map wrapper component
const MapLocationPicker = ({ latitude, longitude, onSelect }) => {
  const defaultCenter = [19.076, 72.8777]; // Mumbai as default
  const parsedLat = latitude ? parseFloat(latitude) : null;
  const parsedLng = longitude ? parseFloat(longitude) : null;

  const position =
    parsedLat != null &&
    !Number.isNaN(parsedLat) &&
    parsedLng != null &&
    !Number.isNaN(parsedLng)
      ? [parsedLat, parsedLng]
      : null;

  return (
    <div className="h-64 w-full rounded-xl overflow-hidden border border-slate-200">
      <MapContainer
        center={position || defaultCenter}
        zoom={13}
        scrollWheelZoom={true}
        className="h-full w-full"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <LocationMarker
          position={position}
          onChange={({ lat, lng }) => {
            onSelect(lat.toFixed(6), lng.toFixed(6));
          }}
        />
      </MapContainer>
    </div>
  );
};

const AddPropertyForm = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // 🔹 Get id from either path params or query (?id=2)
  const { id: pathId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryId = searchParams.get('id');
  const id = pathId || queryId;           // final id
  const isEditMode = Boolean(id);         // true if editing

  const [formData, setFormData] = useState({
    // Basic Information
    propertyName: '',
    clientId: '',
    propertyType: '',

    // Address Information
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
    country: 'India',

    // GPS Location
    latitude: '',
    longitude: '',

    // Additional
    notes: '',
  });

  // Checklist state
  const [checklistItems, setChecklistItems] = useState([
    { id: 1, item: 'Gate locked?', order: 1 },
    { id: 2, item: 'Lights off?', order: 2 },
    { id: 3, item: 'Water leak?', order: 3 },
  ]);
  const [newChecklistItem, setNewChecklistItem] = useState('');

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [showChecklistSection, setShowChecklistSection] = useState(true);
  // ✅ New helpers to match AdminLogin
const getAuthToken = () =>
  localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

const getAuthRole = () =>
  localStorage.getItem('authRole') || sessionStorage.getItem('authRole');

  // 🔹 Clients from DB
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState('');

  const getAdminToken = () =>
    localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');

  const indianStates = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
  ];

    const logoutAndRedirect = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRole');
    navigate('/');
  };
  // 🔹 Fetch clients from backend
  useEffect(() => {
    const fetchClients = async () => {
      setClientsLoading(true);
      setClientsError('');
       const token = getAuthToken();
    const role = getAuthRole();

    if (!token || role !== 'admin') {
      alert('Session expired. Please log in again.');
      logoutAndRedirect();
      return;
    }

      try {
        const url = `https://patrol-visit.patroll-management.nissiindia.com/api/clients`;

        const res = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Unauthorized. Please login again.');
          }
          throw new Error('Failed to fetch clients');
        }

        const data = await res.json();

        const list = Array.isArray(data.clients) ? data.clients : [];

        setClients(list);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setClientsError(err.message || 'Failed to fetch clients');
      } finally {
        setClientsLoading(false);
      }
    };

    fetchClients();
  }, [navigate]);

  // 🔹 If editing, fetch existing property details
  useEffect(() => {
    if (!isEditMode) return;

    const fetchProperty = async () => {
       const token = getAuthToken();
    const role = getAuthRole();

    if (!token || role !== 'admin') {
      alert('Session expired. Please log in again.');
      logoutAndRedirect();
      return;
    }
      try {
        const res = await fetch(`https://patrol-visit.patroll-management.nissiindia.com/api/properties/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error('Failed to fetch property');
        }

        const data = await res.json();
        const p = data.data || data;

        // 🔹 Map backend fields (supports both camelCase & snake_case)
        setFormData({
          propertyName: p.propertyName || p.property_name || '',
          clientId: String(p.clientId || p.client_id || ''),
          propertyType: p.propertyType || p.property_type || '',
          addressLine1: p.addressLine1 || p.address_line1 || '',
          addressLine2: p.addressLine2 || p.address_line2 || '',
          city: p.city || '',
          state: p.state || '',
          pincode: p.pincode || '',
          landmark: p.landmark || '',
          country: p.country || 'India',
          latitude: p.latitude || '',
          longitude: p.longitude || '',
          notes: p.notes || '',
        });

        // 🔹 Checklist from property
        let checklistRaw = [];
        if (Array.isArray(p.checklist)) {
          checklistRaw = p.checklist;
        } else if (p.checklist_json) {
          try {
            const parsed = JSON.parse(p.checklist_json);
            if (Array.isArray(parsed)) checklistRaw = parsed;
          } catch (e) {
            console.warn('Failed to parse checklist_json', e);
          }
        }

        if (checklistRaw.length) {
          setChecklistItems(
            checklistRaw.map((item, index) => ({
              id: item.id || index + 1,
              item: item.item || '',
              order: item.order || index + 1,
            }))
          );
        } else {
          setChecklistItems([
            { id: 1, item: 'Gate locked?', order: 1 },
            { id: 2, item: 'Lights off?', order: 2 },
            { id: 3, item: 'Water leak?', order: 3 },
          ]);
        }
      } catch (err) {
        console.error('Error fetching property:', err);
        setErrors((prev) => ({
          ...prev,
          submit: err.message || 'Failed to load property details',
        }));
      }
    };

    fetchProperty();
  }, [isEditMode, id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: '',
      submit: '',
    }));
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setChecklistItems([
        ...checklistItems,
        {
          id: Date.now(),
          item: newChecklistItem,
          order: checklistItems.length + 1,
        },
      ]);
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (idToRemove) => {
    setChecklistItems(checklistItems.filter((item) => item.id !== idToRemove));
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic Information
    if (!formData.propertyName.trim()) {
      newErrors.propertyName = 'Property name is required';
    }

    if (!formData.clientId) {
      newErrors.clientId = 'Please select a client';
    }

    if (!formData.propertyType) {
      newErrors.propertyType = 'Property type is required';
    }

    // Address Validation
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address line 1 is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Enter a valid 6-digit pincode';
    }

    // GPS validation (optional fields)
    const { latitude, longitude } = formData;
    if (latitude && isNaN(parseFloat(latitude))) {
      newErrors.latitude = 'Latitude must be a number';
    }
    if (longitude && isNaN(parseFloat(longitude))) {
      newErrors.longitude = 'Longitude must be a number';
    }

    if (checklistItems.length === 0) {
      newErrors.checklist = 'Please add at least one checklist item';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      const propertyData = {
        ...formData,
        checklist: checklistItems,
      };
     const token = getAuthToken();
    const role = getAuthRole();

    if (!token || role !== 'admin') {
      alert('Session expired. Please log in again.');
      logoutAndRedirect();
      return;
    }

      const url = isEditMode
        ? `https://patrol-visit.patroll-management.nissiindia.com/api/properties/${id}`
        : 'https://patrol-visit.patroll-management.nissiindia.com/api/properties/add';
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(propertyData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.message ||
            (isEditMode ? 'Failed to update property' : 'Failed to add property')
        );
      }

      setSuccess(true);
      setErrors({});
      // Redirect to list page
      setTimeout(() => {
        navigate('/properties');
      }, 1500);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit:
          error.message ||
          (isEditMode
            ? 'Failed to update property. Please try again.'
            : 'Failed to add property. Please try again.'),
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      propertyName: '',
      clientId: '',
      propertyType: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      landmark: '',
      country: 'India',
      latitude: '',
      longitude: '',
      notes: '',
    });
    setChecklistItems([
      { id: 1, item: 'Gate locked?', order: 1 },
      { id: 2, item: 'Lights off?', order: 2 },
      { id: 3, item: 'Water leak?', order: 3 },
    ]);
    setNewChecklistItem('');
    setErrors({});
    setSuccess(false);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
              }}
            >
              <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-slate-800 truncate">
                {isEditMode ? 'Edit Property' : 'Add New Property'}
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 mt-1 hidden sm:block">
                {isEditMode
                  ? 'Update property details in the patrol system'
                  : 'Add a new property to the patrol system'}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/properties')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
            type="button"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Status banner */}
        {success && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">
                {isEditMode
                  ? 'Property updated successfully'
                  : 'Property added successfully'}
              </p>
              <p className="text-xs text-emerald-700">
                Redirecting to properties list...
              </p>
            </div>
          </div>
        )}

        {errors.submit && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">
                {isEditMode ? 'Failed to update property' : 'Failed to add property'}
              </p>
              <p className="text-xs text-red-700 mt-1">{errors.submit}</p>
            </div>
          </div>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-6 pb-4 border-b border-slate-100">
            <div
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${theme.primary[100]}, ${theme.secondary[100]})`,
              }}
            >
              <Building2
                className="w-4 h-4 sm:w-5 sm:h-5"
                style={{ color: theme.primary[600] }}
              />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-800">
                Basic Information
              </h2>
              <p className="text-xs text-slate-600">
                Key details about the property
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Property Name */}
            <div className="md:col-span-2">
              <label
                htmlFor="propertyName"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Property Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  id="propertyName"
                  name="propertyName"
                  value={formData.propertyName}
                  onChange={handleChange}
                  placeholder="e.g., Sunset Plaza - Building A"
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.propertyName
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200'
                  }`}
                  style={
                    !errors.propertyName
                      ? { '--tw-ring-color': `${theme.primary[500]}33` }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.propertyName) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.propertyName) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.propertyName && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.propertyName}
                </p>
              )}
            </div>

            {/* Client */}
            <div>
              <label
                htmlFor="clientId"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Client <span className="text-red-500">*</span>
              </label>

              {clientsLoading ? (
                <div className="text-xs text-slate-500">Loading clients...</div>
              ) : clientsError ? (
                <div className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {clientsError}
                </div>
              ) : (
                <select
                  id="clientId"
                  name="clientId"
                  value={formData.clientId}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.clientId
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200'
                  }`}
                  style={
                    !errors.clientId
                      ? { '--tw-ring-color': `${theme.primary[500]}33` }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.clientId) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.clientId) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                >
                  <option value="">Select Client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name ||
                        client.client_name ||
                        client.company_name ||
                        `Client #${client.id}`}
                    </option>
                  ))}
                </select>
              )}

              {errors.clientId && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.clientId}
                </p>
              )}
            </div>

            {/* Property Type */}
            <div>
              <label
                htmlFor="propertyType"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Property Type <span className="text-red-500">*</span>
              </label>
              <select
                id="propertyType"
                name="propertyType"
                value={formData.propertyType}
                onChange={handleChange}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                  errors.propertyType
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                    : 'border-slate-200'
                }`}
                style={
                  !errors.propertyType
                    ? { '--tw-ring-color': `${theme.primary[500]}33` }
                    : {}
                }
                onFocus={(e) => {
                  if (!errors.propertyType) {
                    e.target.style.borderColor = theme.primary[500];
                  }
                }}
                onBlur={(e) => {
                  if (!errors.propertyType) {
                    e.target.style.borderColor = 'rgb(226 232 240)';
                  }
                }}
              >
                <option value="">Select type</option>
                <option value="Commercial">Commercial</option>
                <option value="Residential">Residential</option>
                <option value="Industrial">Industrial</option>
                <option value="Mixed-use">Mixed-use</option>
                <option value="Other">Other</option>
              </select>
              {errors.propertyType && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.propertyType}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Address & Location */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-6 pb-4 border-b border-slate-100">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-800">
                Address & Location
              </h2>
              <p className="text-xs text-slate-600">
                Complete property address and GPS coordinates
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Address Line 1 */}
            <div className="md:col-span-2">
              <label
                htmlFor="addressLine1"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Address Line 1 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Home className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  id="addressLine1"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  placeholder="Building/Plot No., Street Name"
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.addressLine1
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200'
                  }`}
                  style={
                    !errors.addressLine1
                      ? { '--tw-ring-color': `${theme.primary[500]}33` }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.addressLine1) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.addressLine1) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.addressLine1 && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.addressLine1}
                </p>
              )}
            </div>

            {/* Address Line 2 */}
            <div className="md:col-span-2">
              <label
                htmlFor="addressLine2"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Address Line 2{' '}
                <span className="text-slate-400 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                id="addressLine2"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleChange}
                placeholder="Apartment, Floor, Landmark"
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all"
                style={{
                  '--tw-ring-color': `${theme.primary[500]}33`,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.primary[500];
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgb(226 232 240)';
                }}
              />
            </div>

            {/* City */}
            <div>
              <label
                htmlFor="city"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter city"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                  errors.city
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                    : 'border-slate-200'
                }`}
                style={
                  !errors.city
                    ? { '--tw-ring-color': `${theme.primary[500]}33` }
                    : {}
                }
                onFocus={(e) => {
                  if (!errors.city) {
                    e.target.style.borderColor = theme.primary[500];
                  }
                }}
                onBlur={(e) => {
                  if (!errors.city) {
                    e.target.style.borderColor = 'rgb(226 232 240)';
                  }
                }}
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.city}
                </p>
              )}
            </div>

            {/* State */}
            <div>
              <label
                htmlFor="state"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                State <span className="text-red-500">*</span>
              </label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                  errors.state
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                    : 'border-slate-200'
                }`}
                style={
                  !errors.state
                    ? { '--tw-ring-color': `${theme.primary[500]}33` }
                    : {}
                }
                onFocus={(e) => {
                  if (!errors.state) {
                    e.target.style.borderColor = theme.primary[500];
                  }
                }}
                onBlur={(e) => {
                  if (!errors.state) {
                    e.target.style.borderColor = 'rgb(226 232 240)';
                  }
                }}
              >
                <option value="">Select state</option>
                {indianStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.state}
                </p>
              )}
            </div>

            {/* Pincode */}
            <div>
              <label
                htmlFor="pincode"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                placeholder="6-digit pincode"
                maxLength={6}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                  errors.pincode
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                    : 'border-slate-200'
                }`}
                style={
                  !errors.pincode
                    ? { '--tw-ring-color': `${theme.primary[500]}33` }
                    : {}
                }
                onFocus={(e) => {
                  if (!errors.pincode) {
                    e.target.style.borderColor = theme.primary[500];
                  }
                }}
                onBlur={(e) => {
                  if (!errors.pincode) {
                    e.target.style.borderColor = 'rgb(226 232 240)';
                  }
                }}
              />
              {errors.pincode && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.pincode}
                </p>
              )}
            </div>

            {/* Landmark */}
            <div>
              <label
                htmlFor="landmark"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Landmark{' '}
                <span className="text-slate-400 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                id="landmark"
                name="landmark"
                value={formData.landmark}
                onChange={handleChange}
                placeholder="Nearby landmark or area"
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all"
                style={{
                  '--tw-ring-color': `${theme.primary[500]}33`,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.primary[500];
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgb(226 232 240)';
                }}
              />
            </div>

            {/* Country */}
            <div>
              <label
                htmlFor="country"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Country
              </label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all"
                style={{
                  '--tw-ring-color': `${theme.primary[500]}33`,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.primary[500];
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgb(226 232 240)';
                }}
              />
            </div>
          </div>

          {/* GPS section */}
          <div className="mt-6 pt-4 border-t border-slate-100">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-50 flex-shrink-0">
                <Navigation className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-slate-800">
                    GPS Location (Optional)
                  </p>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">
                    <Map className="w-3 h-3" />
                    Helps with navigation
                  </span>
                </div>
                <p className="text-xs text-slate-600">
                  Add GPS coordinates for precise location tracking. Guards can
                  navigate directly to this property.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Latitude */}
              <div>
                <label
                  htmlFor="latitude"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Latitude
                </label>
                <input
                  type="text"
                  id="latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  placeholder="19.0760"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.latitude
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200'
                  }`}
                  style={
                    !errors.latitude
                      ? { '--tw-ring-color': `${theme.primary[500]}33` }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.latitude) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.latitude) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
                {errors.latitude && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.latitude}
                  </p>
                )}
              </div>

              {/* Longitude */}
              <div>
                <label
                  htmlFor="longitude"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Longitude
                </label>
                <input
                  type="text"
                  id="longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  placeholder="72.8777"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.longitude
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200'
                  }`}
                  style={
                    !errors.longitude
                      ? { '--tw-ring-color': `${theme.primary[500]}33` }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.longitude) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.longitude) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
                {errors.longitude && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.longitude}
                  </p>
                )}
              </div>
            </div>

            {/* Map Picker */}
            <div className="mt-4 space-y-2">
              <p className="text-xs text-slate-600">
                Click on the map to set latitude &amp; longitude. Current:{' '}
                {formData.latitude && formData.longitude
                  ? `${formData.latitude}, ${formData.longitude}`
                  : 'Not set'}
              </p>

              <MapLocationPicker
                latitude={formData.latitude}
                longitude={formData.longitude}
                onSelect={(lat, lng) => {
                  setFormData((prev) => ({
                    ...prev,
                    latitude: String(lat),
                    longitude: String(lng),
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    latitude: '',
                    longitude: '',
                  }));
                }}
              />
            </div>
          </div>
        </div>

        {/* Checklist */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
          <div className="flex items-center justify-between gap-3 mb-4 sm:mb-6 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-violet-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-800">
                  Property Checklist
                </h2>
                <p className="text-xs text-slate-600">
                  Tasks guards should verify during each visit
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowChecklistSection((prev) => !prev)}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors flex items-center gap-1 text-xs sm:text-sm text-slate-600"
            >
              {showChecklistSection ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show
                </>
              )}
            </button>
          </div>

          {showChecklistSection && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 text-xs sm:text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-xl p-3 sm:p-4">
                <Info className="w-4 h-4 text-slate-400 mt-0.5" />
                <p>
                  Define the key checks for this property (e.g., gates locked,
                  lights off, doors secured). These will appear on the guard’s
                  mobile app for every visit.
                </p>
              </div>

              {/* Existing checklist */}
              <div className="space-y-2">
                {checklistItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-3 sm:px-4 py-2"
                  >
                    <span className="w-6 text-xs font-semibold text-slate-500">
                      {index + 1}.
                    </span>
                    <p className="flex-1 text-sm text-slate-800">
                      {item.item}
                    </p>
                    <button
                      type="button"
                      onClick={() => removeChecklistItem(item.id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add checklist item */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-3">
                <input
                  type="text"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  placeholder="e.g., Check fire extinguisher"
                  className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all text-sm sm:text-base"
                  style={{
                    '--tw-ring-color': `${theme.primary[500]}33`,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary[500];
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgb(226 232 240)';
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addChecklistItem();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={addChecklistItem}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex-shrink-0 flex items-center justify-center gap-2"
                  style={{
                    background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
                  }}
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Add Item</span>
                </button>
              </div>

              {errors.checklist && (
                <p className="text-xs sm:text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.checklist}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <X className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                Additional Notes
              </h2>
              <p className="text-xs text-slate-600">
                Any extra information or special instructions
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-semibold text-slate-700 mb-2"
            >
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Add any special instructions, maintenance notes, or important information about this property..."
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all resize-none"
              style={{
                '--tw-ring-color': `${theme.primary[500]}33`,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.primary[500];
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgb(226 232 240)';
              }}
            ></textarea>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
            <button
              type="button"
              onClick={handleReset}
              className="w-full sm:w-auto px-6 py-3 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all duration-200 order-2 sm:order-1"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-6 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 shadow-sm hover:shadow-lg transition-all duration-200 order-1 sm:order-2 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
              }}
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Property' : 'Save Property'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Simple fade-in animation */}
      <style jsx="true">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AddPropertyForm;
